import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/data/products";
import { Minus, Plus, X } from "lucide-react";

export default function CartPage() {
  const { items, update, remove, subtotal } = useCart();
  const shipping = 0;
  const tax = Math.round(subtotal * 0.05);
  const total = subtotal + shipping + tax;

  return (
    <div className="pt-32 pb-24 bg-ivory min-h-screen" data-testid="cart-page">
      <div className="container-lux">
        <p className="overline text-muted-foreground">Your Bag</p>
        <h1 className="mt-4 font-serif tracking-tighter text-5xl md:text-7xl font-light">Shopping Bag</h1>

        {items.length === 0 ? (
          <div className="mt-24 text-center">
            <p className="font-serif text-3xl">Your bag is empty</p>
            <Link to="/collections" className="lux-btn-outline mt-10">Discover Collections</Link>
          </div>
        ) : (
          <div className="mt-16 grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <div className="divider-thin" />
              {items.map((i, idx) => (
                <div key={idx} className="flex gap-6 py-8 border-b border-border">
                  <div className="w-32 h-44 bg-muted overflow-hidden">
                    <img src={i.image} alt={i.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1">
                    <p className="font-serif text-xl">{i.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">{i.subtitle}</p>
                    {i.size && <p className="overline mt-2 text-muted-foreground">Size · {i.size}</p>}
                    {i.color && <p className="overline text-muted-foreground">Colour · {i.color}</p>}
                    <div className="mt-6 flex items-center border border-border w-max">
                      <button onClick={() => update(idx, i.quantity-1)} className="p-3"><Minus size={12} strokeWidth={1} /></button>
                      <span className="px-4 text-sm">{i.quantity}</span>
                      <button onClick={() => update(idx, i.quantity+1)} className="p-3"><Plus size={12} strokeWidth={1} /></button>
                    </div>
                  </div>
                  <div className="text-right flex flex-col justify-between items-end">
                    <button onClick={() => remove(idx)}><X size={16} strokeWidth={1} /></button>
                    <p className="font-serif text-lg">{formatPrice(i.price * i.quantity)}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-sand/60 p-10 h-max">
              <p className="overline">Order Summary</p>
              <div className="mt-8 space-y-3 text-sm">
                <Row label="Subtotal" value={formatPrice(subtotal)} />
                <Row label="Shipping" value={shipping === 0 ? "Complimentary" : formatPrice(shipping)} />
                <Row label="Tax (5%)" value={formatPrice(tax)} />
              </div>
              <div className="divider-thin my-6" />
              <Row label="Total" value={formatPrice(total)} big />
              <Link to="/checkout" data-testid="proceed-checkout" className="lux-btn-dark w-full mt-8">Proceed To Checkout</Link>

              <div className="mt-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 divider-thin" />
                  <span className="overline text-muted-foreground text-[9px]">Or Pay Instantly</span>
                  <div className="flex-1 divider-thin" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Link
                    to="/checkout?express=upi"
                    data-testid="express-upi"
                    className="border border-ink hover:bg-ink hover:text-ivory transition-colors py-3 px-4 flex items-center justify-center gap-2 text-[11px] uppercase tracking-widest"
                  >
                    <span className="font-serif text-base normal-case tracking-normal font-medium">UPI</span>
                    <span className="opacity-70">→</span>
                  </Link>
                  <Link
                    to="/checkout?express=gpay"
                    data-testid="express-gpay"
                    className="border border-ink hover:bg-ink hover:text-ivory transition-colors py-3 px-4 flex items-center justify-center gap-2 text-[11px] uppercase tracking-widest"
                  >
                    <span className="font-serif text-base normal-case tracking-normal font-medium">G Pay</span>
                    <span className="opacity-70">→</span>
                  </Link>
                </div>
                <p className="text-[10px] text-muted-foreground mt-3 text-center">
                  Skips extra steps · pay in one tap on mobile
                </p>
              </div>

              <Link to="/collections" className="lux-link mt-6 block text-center">Continue Shopping</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Row = ({ label, value, big }) => (
  <div className="flex justify-between">
    <span className={big ? "overline" : "text-muted-foreground"}>{label}</span>
    <span className={big ? "font-serif text-2xl" : ""}>{value}</span>
  </div>
);
